import {
  type AssetsHttpGetSharesAllQuery,
  httpDeleteSharesBulk,
  httpDeleteSharesBulkAll,
  httpGetPlaylists,
  httpGetPortals,
  httpGetShares,
  httpGetSharesAll,
} from '@iconik-io/iconik-api/src/services/assets';
import { httpPostSearch } from '@iconik-io/iconik-api/src/services/search';
import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
  OBJECT_TYPE_PLAYLISTS,
  OBJECT_TYPE_PORTALS,
} from '@iconik-io/iconik-api/src/types/object_types';
import { deleteNormalizedEntity } from '@iconik-io/shared-lib/src/redux/entities/slice';
import { getSortQuery } from '@iconik-io/shared-lib/src/utils/sort/get_sort_query';

import type { AppDispatch, GetState } from '../../../store/types';
import { addOneSecondToRightBoundary } from '../../../utils/add_one_second_to_right_boundary';
import { getSearchDocument } from '../../../utils/get_search_document';
import { selectUserRoles } from '../../../utils/get_user_roles';
import { prepareUrlQuery } from '../../../utils/prepare_url_query';
import { guardFilterRange } from '../../../utils/search_utils/guard_filter';
import {
  type CommonListGetParams,
  wrapCommonDelete,
  wrapCommonGetList,
} from '../../common_list/thunks';
import { getCanAccessPortals } from '../../settings/select_features';

type SharesListGetParams = CommonListGetParams & { ids?: string[] };

const PAGE_SIZE = 50;

export function getAdminShares(
  params: SharesListGetParams = {},
  debounce?: boolean,
) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const roles = selectUserRoles(getState());
    const pageState = getState().pages.adminShares;

    // Depending on the roles the user has, we either fetch all shares or only the shares the user has created
    let getSharesRequest;
    if (roles.canManageAllShares) {
      getSharesRequest = httpGetSharesAll;
    } else if (roles.canWriteShares) {
      getSharesRequest = httpGetShares;
    } else {
      // We should never end up here unless there is a programming error
      return Promise.reject(
        new Error('User does not have the needed roles for managing shares'),
      );
    }

    return wrapCommonGetList({
      prefix: 'admin_shares',
      dispatch,
      params,
      debounce,
      getListPromise: (listParams) => {
        const filtersPanelState = getState().panelsState.adminSharesFilters;
        const filters = filtersPanelState.filterValues;
        const dateCreated = guardFilterRange(filters.date_created);
        const dateExpires = guardFilterRange(filters.expires);
        const facetValues = filtersPanelState.facetValues;
        // These need to be converted since the API does not handle 0 and 1 as boolean values
        const is_approval = facetValues?.is_approval?.map((value) =>
          value === '1' ? 'true' : 'false',
        );

        return dispatch(
          // biome-ignore lint/plugin: the list sends its filter values by name, which no generated query type can know.
          getSharesRequest({
            page: listParams.page,
            per_page: listParams.per_page ?? PAGE_SIZE,
            ids: params.ids?.join(','),
            ...filtersPanelState.filterValues,
            ...filtersPanelState.facetValues,
            is_approval,
            date_created: dateCreated
              ? addOneSecondToRightBoundary(dateCreated)
              : undefined,
            expires: dateExpires
              ? addOneSecondToRightBoundary(dateExpires)
              : undefined,
            query: prepareUrlQuery(filtersPanelState.query),
            sort: getSortQuery(params.sort ?? pageState.sort),
          } as AssetsHttpGetSharesAllQuery),
        ).then((response) => {
          const items = response.body.objects;
          const assetAndCollectionIds = items
            .filter(
              (item) =>
                item.object_type === OBJECT_TYPE_ASSETS ||
                item.object_type === OBJECT_TYPE_COLLECTIONS,
            )
            .map((item) => item.object_id)
            .filter((id): id is string => Boolean(id));

          const playlistIds = items
            .filter((item) => item.object_type === OBJECT_TYPE_PLAYLISTS)
            .map((item) => item.object_id)
            .filter((id): id is string => Boolean(id));

          const canShowPortals =
            getCanAccessPortals(getState()) && Boolean(roles.canReadPortals);
          const portalIds = items
            .filter(
              (item) =>
                canShowPortals && item.object_type === OBJECT_TYPE_PORTALS,
            )
            .map((item) => item.object_id)
            .filter((id): id is string => Boolean(id));

          if (assetAndCollectionIds.length > 0) {
            dispatch(
              httpPostSearch(
                getSearchDocument(assetAndCollectionIds, [
                  OBJECT_TYPE_ASSETS,
                  OBJECT_TYPE_COLLECTIONS,
                ]),
                {
                  save_search_history: false,
                  per_page: assetAndCollectionIds.length,
                },
              ),
            );
          }

          if (playlistIds.length > 0) {
            dispatch(
              httpGetPlaylists({
                ids: playlistIds.join(','),
                per_page: playlistIds.length,
              }),
            );
          }

          // Search has no `doc_types` for portals, so the cover a portal share
          // shows has to come from the portals endpoint.
          if (portalIds.length > 0) {
            dispatch(
              httpGetPortals({
                ids: portalIds.join(','),
                per_page: portalIds.length,
              }),
            );
          }
          return response;
        });
      },
    });
  };
}

export const deleteAdminSharesByIds =
  (shareIds: string[]) => (dispatch: AppDispatch, getState: GetState) => {
    const roles = selectUserRoles(getState());

    // Depending on the roles the user has, we either delete using all shares endpoint
    // or the endpoint that only allows deleting shares the user has created
    let deleteSharesRequest;
    if (roles.canManageAllShares) {
      deleteSharesRequest = httpDeleteSharesBulkAll;
    } else if (roles.canDeleteObjectShares) {
      deleteSharesRequest = httpDeleteSharesBulk;
    } else {
      // We should never end up here unless there is a programming error
      return Promise.reject(
        new Error('User does not have the needed roles for managing shares'),
      );
    }

    return wrapCommonDelete({
      prefix: 'admin_shares',
      dispatch,
      ids: shareIds,
      deletePromise: dispatch(
        deleteSharesRequest({ share_ids: shareIds }),
      ).then(() => {
        // Delete the shares from the share entities as well since these are different and used on the asset page
        for (const id of shareIds) {
          dispatch(deleteNormalizedEntity({ key: 'shares', id }));
        }
      }),
    });
  };
